import { useCepLookup } from '@/hooks/use-cep-lookup';
import { cn } from '@/lib/utils';
import type { SacramentStatus } from 'generated/prisma/enums';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { ChoiceGroup, FormSection, TextField } from './fields';
import { MARRIED_OPTIONS, SACRAMENT_OPTIONS, maskCep, maskPhone } from './sacraments';

/** Valores do formulário do casal (antes da validação/transformação do Zod). */
export interface CoupleFormValues {
  husbandName: string;
  husbandPhone: string;
  wifeName: string;
  wifePhone: string;
  email: string;
  cep: string;
  address: string;
  addressNumber: string;
  addressComplement: string;
  baptismStatus: SacramentStatus | '';
  confirmationStatus: SacramentStatus | '';
  marriageSacrament: boolean | null;
  marriageDate: string;
  parish: string;
  pastoralMovement: string;
  childrenInfo: string;
  eventId: string;
  isActive: boolean;
}

export const EMPTY_COUPLE: CoupleFormValues = {
  husbandName: '',
  husbandPhone: '',
  wifeName: '',
  wifePhone: '',
  email: '',
  cep: '',
  address: '',
  addressNumber: '',
  addressComplement: '',
  baptismStatus: '',
  confirmationStatus: '',
  marriageSacrament: null,
  marriageDate: '',
  parish: '',
  pastoralMovement: '',
  childrenInfo: '',
  eventId: '',
  isActive: true,
};

const COPY = {
  public: {
    couple: 'Quem vai viver o retiro',
    address: 'Onde vocês moram',
    faith: 'A caminhada de vocês na Igreja',
  },
  admin: {
    couple: 'Dados do casal',
    address: 'Onde o casal mora',
    faith: 'Caminhada na Igreja',
  },
};

interface CoupleFieldsProps {
  form: UseFormReturn<CoupleFormValues>;
  variant: 'public' | 'admin';
  sectionClassName?: string;
}

/** Seções 01 (casal), 02 (endereço) e 03 (vida de fé) do formulário do casal. */
export function CoupleFields({ form, variant, sectionClassName }: CoupleFieldsProps) {
  const {
    register,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitted, dirtyFields },
  } = form;
  /** Registra o campo formatando o valor enquanto o usuário digita. */
  const registerMasked = (
    name: 'husbandPhone' | 'wifePhone' | 'cep',
    mask: (v: string) => string,
  ) => {
    const field = register(name);
    return {
      ...field,
      maxLength: name === 'cep' ? 9 : 15,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.value = mask(e.target.value);
        return field.onChange(e);
      },
    };
  };
  const copy = COPY[variant];
  const admin = variant === 'admin';

  const married = watch('marriageSacrament');
  const cep = watch('cep');
  // Só busca quando o CEP foi digitado — na edição não sobrescreve o endereço salvo.
  const { hint: cepHint } = useCepLookup(cep, !!dirtyFields.cep, (address) =>
    setValue('address', address, { shouldDirty: true, shouldValidate: isSubmitted }),
  );

  const grid = admin
    ? 'grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]'
    : 'grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]';

  return (
    <>
      <FormSection
        eyebrow="01 • O CASAL"
        title={copy.couple}
        className={sectionClassName}
      >
        <div className={grid}>
          <TextField
            label="Nome do esposo *"
            placeholder="Ex: João Silva"
            autoComplete="off"
            error={errors.husbandName?.message}
            {...register('husbandName')}
          />
          <TextField
            label="Telefone do esposo *"
            placeholder="(19) 99999-9999"
            type="tel"
            error={errors.husbandPhone?.message}
            {...registerMasked('husbandPhone', maskPhone)}
          />
          <TextField
            label="Nome da esposa *"
            placeholder="Ex: Maria Silva"
            autoComplete="off"
            error={errors.wifeName?.message}
            {...register('wifeName')}
          />
          <TextField
            label="Telefone da esposa *"
            placeholder="(19) 99999-9999"
            type="tel"
            error={errors.wifePhone?.message}
            {...registerMasked('wifePhone', maskPhone)}
          />
          {admin && (
            <TextField
              wrapperClassName="col-span-full"
              label="E-mail *"
              placeholder="casal@email.com"
              type="email"
              error={errors.email?.message}
              {...register('email')}
            />
          )}
        </div>
        {!admin && (
          <TextField
            label="E-mail *"
            placeholder="casal@email.com"
            type="email"
            error={errors.email?.message}
            {...register('email')}
          />
        )}
      </FormSection>

      <FormSection
        eyebrow="02 • ENDEREÇO"
        title={copy.address}
        className={sectionClassName}
      >
        {admin ? (
          <div className={grid}>
            <TextField
              label="CEP *"
              placeholder="00000-000"
              inputMode="numeric"
              hint={cepHint}
              error={errors.cep?.message}
              {...registerMasked('cep', maskCep)}
            />
            <TextField
              label="Número *"
              placeholder="100"
              error={errors.addressNumber?.message}
              {...register('addressNumber')}
            />
            <TextField
              wrapperClassName="col-span-full"
              label="Endereço *"
              placeholder="Rua, bairro, cidade/UF"
              error={errors.address?.message}
              {...register('address')}
            />
            <TextField
              wrapperClassName="col-span-full"
              label="Complemento"
              placeholder="Apto, bloco..."
              {...register('addressComplement')}
            />
          </div>
        ) : (
          <>
            <TextField
              label="CEP *"
              placeholder="00000-000"
              inputMode="numeric"
              hint={cepHint}
              error={errors.cep?.message}
              {...registerMasked('cep', maskCep)}
            />
            <TextField
              label="Endereço *"
              placeholder="Rua, bairro, cidade/UF"
              error={errors.address?.message}
              {...register('address')}
            />
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4">
              <TextField
                label="Número *"
                placeholder="100"
                error={errors.addressNumber?.message}
                {...register('addressNumber')}
              />
              <TextField
                label="Complemento"
                placeholder="Apto, bloco..."
                {...register('addressComplement')}
              />
            </div>
          </>
        )}
      </FormSection>

      <FormSection
        eyebrow="03 • VIDA DE FÉ"
        title={copy.faith}
        className={cn('gap-6', sectionClassName)}
      >
        <Controller
          control={control}
          name="baptismStatus"
          render={({ field, fieldState }) => (
            <ChoiceGroup
              label="Situação do Batismo *"
              options={SACRAMENT_OPTIONS}
              value={field.value || null}
              onChange={field.onChange}
              error={fieldState.error ? 'Selecione uma opção.' : undefined}
            />
          )}
        />
        <Controller
          control={control}
          name="confirmationStatus"
          render={({ field, fieldState }) => (
            <ChoiceGroup
              label="Situação da Crisma *"
              options={SACRAMENT_OPTIONS}
              value={field.value || null}
              onChange={field.onChange}
              error={fieldState.error ? 'Selecione uma opção.' : undefined}
            />
          )}
        />
        <Controller
          control={control}
          name="marriageSacrament"
          render={({ field, fieldState }) => (
            <ChoiceGroup
              label="São casados na Igreja? *"
              options={MARRIED_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error ? 'Selecione uma opção.' : undefined}
            />
          )}
        />
        {married === true && (
          <div className="max-w-[260px]">
            <TextField
              label="Data do matrimônio *"
              type="date"
              error={errors.marriageDate?.message}
              {...register('marriageDate')}
            />
          </div>
        )}
        <div className={grid}>
          <TextField
            label="Paróquia"
            placeholder="Ex: Paróquia Santa Bárbara"
            {...register('parish')}
          />
          <TextField
            label="Pastoral / Movimento"
            placeholder="Ex: Pastoral Familiar"
            {...register('pastoralMovement')}
          />
        </div>
      </FormSection>
    </>
  );
}
